// Brand identity constants shared across api + web.
//
// Domain strategy (Barry, 2026-04-27 / locked 2026-04-30,
// post-custom-domain-flip 2026-05-04): the production domain
// `manos.team` is now the unified domain for both customer-facing
// copy AND the Gadget runtime / email transport. Gadget enforces
// that any from-address sent via their email transport ends in the
// app's primary domain (now manos.team), so the previous split
// (public manos.team vs runtime manos.app) is collapsed -- all
// defaults below land on manos.team. Inbound reply routing is the
// only thing that may stay on a different host (DNS-bound).
// Override per environment via the MANOS_DOMAIN env var.

/** Primary public-facing domain. Drives support / sales / website
 *  / footer / AASA / assetlinks constants below. Override per
 *  environment via env MANOS_DOMAIN. */
export const MANOS_DOMAIN: string = process.env.MANOS_DOMAIN || "manos.team";

// ----------------------------------------------------------------- mobile-link config
//
// Universal Links / App Links require the AASA + assetlinks files
// served at the production hostname. These config values flow into
// the /.well-known/* routes. Until the production hostname is
// locked, placeholders ship and the apps fall back to the
// manos:// custom scheme. See plan:
// docs/plans/manos-apprentice-mobile-navigation-parity.md.

/**
 * Apple Developer Team ID. Visit
 * https://developer.apple.com -> Membership -> Team ID.
 * Format: 10-char alphanumeric.
 * Default placeholder works for non-production environments where
 * Universal Link verification is not exercised.
 */
export const MANOS_APPLE_TEAM_ID: string =
  process.env.MANOS_APPLE_TEAM_ID || "TEAMID";

/**
 * iOS bundle identifier. Mirrors PRODUCT_BUNDLE_IDENTIFIER in
 * apps/manos-ios/Manos.xcodeproj. Override only if the bundle
 * id ever changes; the default matches the current iOS app.
 */
export const MANOS_IOS_BUNDLE_ID: string =
  process.env.MANOS_IOS_BUNDLE_ID || "com.getmanos.app";

/**
 * Android package name. Mirrors `applicationId` in
 * apps/manos-android/app/build.gradle.kts.
 */
export const MANOS_ANDROID_PACKAGE: string =
  process.env.MANOS_ANDROID_PACKAGE || "uk.co.barrykilby.manos";

/**
 * Android upload-key SHA-256 fingerprint. 64 hex chars,
 * uppercase, colon-separated. Get via:
 *   keytool -list -v -keystore <keystore.jks> -alias <alias>
 * If shipping via Play App Signing, ALSO add the Play-managed
 * fingerprint to MANOS_ANDROID_SHA256_FINGERPRINTS as
 * comma-separated values.
 */
export const MANOS_ANDROID_SHA256_FINGERPRINTS: string[] =
  (process.env.MANOS_ANDROID_SHA256_FINGERPRINTS || "SHA256_FINGERPRINT_PLACEHOLDER")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/** The amber Manos calls its brand colour. Used by all email CTAs. */
export const MANOS_BRAND_AMBER = "#d97706";

// ----------------------------------------------------------------- legal entity
//
// Canonical Companies House details for the legal entity behind the
// product. Used on privacy / terms pages (UK GDPR Article 13 + DPA
// 2018 require the data controller to be identifiable), invoice
// footers, email "regulated entity" disclosures, etc.
//
// Source of truth: Companies House (https://find-and-update.company-
// information.service.gov.uk/company/17187910). If the entity is ever
// renamed / moved / restructured, update here -- everywhere downstream
// reads from these constants.

/** Registered legal entity name as it appears on Companies House. */
export const MANOS_LEGAL_ENTITY = "Manos Holdings Ltd";

/** Companies House registration number for MANOS_LEGAL_ENTITY. */
export const MANOS_COMPANY_NUMBER = "17187910";

/** Single-line registered office address. */
export const MANOS_REGISTERED_OFFICE =
  "66 Paul Street, London, England, EC2A 4NA";

// ----------------------------------------------------------------- contact

/**
 * Generic support inbox shown on public-facing pages (privacy /
 * terms / cookies / footer). Derives from MANOS_DOMAIN so flipping
 * the brand domain swaps the inbox in lockstep. Override directly
 * via MANOS_SUPPORT_EMAIL if the support inbox lives on a different
 * domain than the marketing site.
 */
export const MANOS_SUPPORT_EMAIL: string =
  process.env.MANOS_SUPPORT_EMAIL || `support@${MANOS_DOMAIN}`;

/**
 * Sales / commercial inbox for the in-app surfaces (subscription
 * page, Enterprise enquiry CTA). Same domain as support by default;
 * override independently via MANOS_SALES_EMAIL.
 */
export const MANOS_SALES_EMAIL: string =
  process.env.MANOS_SALES_EMAIL || `hello@${MANOS_DOMAIN}`;

/**
 * Outbound system email address. Stripe webhook + transactional mail
 * default to this when MANOS_FROM_EMAIL env override is unset. Must
 * end in the app's Gadget primary domain (post-flip 2026-05-04 that
 * is `manos.team`); Gadget's email transport rejects from-addresses
 * on any other domain.
 */
export const MANOS_FROM_EMAIL_DEFAULT: string =
  process.env.MANOS_FROM_EMAIL || "noreply@manos.team";

/**
 * Reply-routing inbox domain for inbound email replies. Manos's email
 * router parses `reply+{workItemId}@inbound.manos.app` and threads the
 * reply onto the work item's activity log. Tied to the inbound mail
 * processor (MX records), not Gadget's primaryDomain. Stays on
 * `manos.app` until / unless an MX record is set up on
 * `inbound.manos.team`; override via `MANOS_INBOUND_EMAIL_DOMAIN`.
 */
export const MANOS_INBOUND_EMAIL_DOMAIN: string =
  process.env.MANOS_INBOUND_EMAIL_DOMAIN || "inbound.manos.app";

// ----------------------------------------------------------------- web

/** Marketing site root (legal links, About, etc). Defaults to the
 *  primary domain so the brand reads consistent. */
export const MANOS_WEBSITE_URL: string =
  process.env.MANOS_WEBSITE_URL || `https://${MANOS_DOMAIN}`;

/**
 * Default app origin used by transactional email templates when no
 * env override is present. Production deploys set MANOS_APP_URL.
 */
export const MANOS_APP_URL_DEFAULT: string =
  process.env.MANOS_APP_URL || "https://manos.team";

/**
 * Hostname the Stripe checkout success/return URLs default to when
 * the inbound request doesn't carry an Origin header. Matches the
 * runtime app domain.
 */
export const MANOS_DEFAULT_HOST: string =
  process.env.MANOS_DEFAULT_HOST || "manos.app";

/** Trading entity behind Manos. Used in cookie + privacy copy. */
export const MANOS_COMPANY_NAME = "Virtual Origin Ltd";

/** Public product name. */
export const MANOS_PRODUCT_NAME = "Manos";

/** Print-template footer line (PDF invoices / quotes / credit notes).
 *  Derives from MANOS_DOMAIN so the printed footer matches whatever
 *  the brand domain is set to. */
export const MANOS_PRINT_FOOTER: string =
  process.env.MANOS_PRINT_FOOTER ||
  `Generated by ${MANOS_PRODUCT_NAME} · ${MANOS_DOMAIN}`;
